import { createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";

import logisticsApi from "services/logisticsApi";
import { putCoverageAreaDrivers } from "services/putCoverageAreaDrivers";

import i18next from "i18next";
import {
  CoverageAreaOrderDetailDto,
  CoverageAreaOrderDetailResponse,
  normalizeCoverageAreaOrderDetail,
} from "types/coverageAreaOrderDetail";
import {
  CoverageAreasResponse,
  WorkingHourRequest,
  WorkingHourResponse,
  UpdateWorkingHourResponse,
  UpdateWorkingHourRequest,
  UpdateWorkingAreaResponse,
  UpdateWorkingAreaRequest,
  DeleteWorkingAreaRequest,
  UpdateWorkingAreaStatusRequest,
  UpdateWorkingAreaStatusResponse,
  DeleteWorkingAreaResponse,
  CoverageAreasRequest,
  CoverageAreaDetailRequest,
  CoverageAreaProps,
  CoverageAreaSummary,
  UpdateMapRequest,
  CreateWorkingAreaRequest,
  WorkingHourProps,
  MassUpdateWorkingHoursRequest,
  MassUpdateWorkingHoursResponse,
  SaveMapAreaDriversRequest,
} from "./types.store";
import { enrichCoverageAreaDriversFromApi } from "components/CardDriverArea/coverageAreaDrivers";
import { ECoverageAreaType } from "enums/options/coverageArea";
import {
  buildLogisticsCoverageAreaBody,
  normalizeCoverageAreaFromApi,
} from "./logisticsCoverageArea.helpers";

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function unwrapData(raw: unknown): unknown {
  return isRecord(raw) && "data" in raw ? raw.data : raw;
}

function normalizeCoverageAreaDetail(raw: unknown): CoverageAreaProps | null {
  const payload = unwrapData(raw);
  if (
    !isRecord(payload) ||
    typeof payload.id !== "string" ||
    typeof payload.name !== "string" ||
    !isRecord(payload.dealer)
  ) {
    return null;
  }

  const normalized = normalizeCoverageAreaFromApi(
    payload as unknown as CoverageAreaProps,
  );

  return {
    ...normalized,
    ...enrichCoverageAreaDriversFromApi(normalized),
    openingHours: Array.isArray(payload.openingHours)
      ? payload.openingHours
      : [],
    holidayOpeningHours: Array.isArray(payload.holidayOpeningHours)
      ? payload.holidayOpeningHours
      : [],
    holidays: Array.isArray(payload.holidays) ? payload.holidays : [],
  };
}

function normalizeCoverageAreaSummaries(raw: unknown): CoverageAreaSummary[] {
  const payload = unwrapData(raw);
  if (!Array.isArray(payload)) return [];

  return payload
    .filter(
      (area): area is { id: string; name: string } =>
        isRecord(area) &&
        typeof area.id === "string" &&
        typeof area.name === "string",
    )
    .map(({ id, name }) => ({ id, name }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

const inflightCoverageAreaRequests = new Map<
  string,
  Promise<CoverageAreaProps>
>();

function buildCreateWorkingAreaBody(data: CreateWorkingAreaRequest) {
  if (data.type === ECoverageAreaType.DealerMap) {
    return {
      name: data.name,
      mapLink: data.mapLink,
      type: data.type,
    };
  }

  return {
    name: data.name,
    type: data.type,
  };
}

export const handleGetWorkingAreasRequest = createAsyncThunk<
  CoverageAreasResponse,
  CoverageAreasRequest
>("getWorkingAreas", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));
    const response = await logisticsApi.get(
      `/v1/dealers/${data.dealerId}/coverage-areas`,
    );

    return normalizeCoverageAreaSummaries(response.data);
  } catch (err) {
    toast(i18next.t("failedLoadAreasPleaseAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleGetWorkingAreaRequest = createAsyncThunk<
  CoverageAreaProps,
  CoverageAreaDetailRequest
>("getWorkingArea", async (params, { rejectWithValue, dispatch }) => {
  const shouldShowSpinner = !params.suppressGlobalSpinner;

  try {
    if (shouldShowSpinner) {
      dispatch(setIsSpinnerVisible(true));
    }

    const requestKey = `${params.dealerId}:${params.coverageAreaId}`;
    const inflightRequest = inflightCoverageAreaRequests.get(requestKey);
    if (inflightRequest) {
      const coverageArea = await inflightRequest;
      return coverageArea;
    }

    const request = logisticsApi
      .get(
        `/v1/dealers/${params.dealerId}/coverage-areas/${params.coverageAreaId}`,
      )
      .then(({ data }) => {
        const coverageArea = normalizeCoverageAreaDetail(data);
        if (!coverageArea) {
          throw new Error("Invalid coverage area response");
        }
        return coverageArea;
      })
      .finally(() => {
        if (inflightCoverageAreaRequests.get(requestKey) === request) {
          inflightCoverageAreaRequests.delete(requestKey);
        }
      });

    inflightCoverageAreaRequests.set(requestKey, request);
    const coverageArea = await request;
    return coverageArea;
  } catch (err) {
    toast(i18next.t("failedLoadAreasPleaseAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    if (shouldShowSpinner) {
      dispatch(setIsSpinnerVisible(false));
    }
  }
});

export const handleUpdateWorkingAreaRequest = createAsyncThunk<
  UpdateWorkingAreaResponse,
  UpdateWorkingAreaRequest
>("putWorkingArea", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const { dealerId, ...updatedArea } = data;

    await logisticsApi.put(
      `/v1/dealers/${dealerId}/coverage-areas/${data.id}`,
      buildLogisticsCoverageAreaBody(updatedArea),
    );

    return updatedArea;
  } catch (err) {
    toast(i18next.t("failedUpdateAreaPleaseAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateWorkingAreaStatusRequest = createAsyncThunk<
  UpdateWorkingAreaStatusResponse,
  UpdateWorkingAreaStatusRequest
>("putWorkingAreaStatus", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    if (data.enabled) {
      await logisticsApi.put(
        `/v1/dealers/${data.dealerId}/coverage-areas/${data.id}/enable`,
      );
    } else {
      await logisticsApi.put(
        `/v1/dealers/${data.dealerId}/coverage-areas/${data.id}/disable`,
      );
    }

    return data;
  } catch (err) {
    toast(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      err?.response?.data?.errors?.[0]?.message ||
        i18next.t("failedUpdateAreaPleaseAgain").toString(),
      {
        type: "error",
        autoClose: false,
      },
    );

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleDeleteWorkingAreaRequest = createAsyncThunk<
  DeleteWorkingAreaResponse,
  DeleteWorkingAreaRequest
>("deleteWorkingArea", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await logisticsApi.delete(
      `/v1/dealers/${data.dealerId}/coverage-areas/${data.id}`,
    );

    return data;
  } catch (err) {
    toast(i18next.t("failedDeleteAreaAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleCreateWorkingAreaRequest = createAsyncThunk<
  null,
  CreateWorkingAreaRequest
>("postWorkingArea", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await logisticsApi.post(
      `/v1/dealers/${data.dealerId}/coverage-areas`,
      buildCreateWorkingAreaBody(data),
    );

    await dispatch(handleGetWorkingAreasRequest({ dealerId: data.dealerId }));

    return null;
  } catch (err) {
    toast(i18next.t("failedCreateAreaAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleGetWorkingHourRequest = createAsyncThunk<
  WorkingHourResponse,
  WorkingHourRequest
>("getWorkingHour", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));
    const response = await logisticsApi.get(
      `/working-area/${data.workingAreaId}/hours?api-version=1`,
    );

    return response.data;
  } catch (err) {
    toast(i18next.t("failedLoadSchedulesPleaseAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateWorkingHourRequest = createAsyncThunk<
  UpdateWorkingHourResponse,
  UpdateWorkingHourRequest
>("putWorkingHour", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const openingHoursToPersist: WorkingHourProps[] = [];
    data.openingHours.forEach((openingHour) => {
      const openingHourToPersist: WorkingHourProps = {
        ...openingHour,
      };
      // TODO: conversor de horas para UTC
      // if (openingHourToPersist.close) {
      //   const [hour, minutes] = openingHourToPersist.close.split(":");
      //   openingHourToPersist.close = ChamaDate.fromHoursAndMinutes(
      //     +hour,
      //     +minutes,
      //   ).toPersistanceHoursAndMinutes();
      // }

      // if (openingHourToPersist.open) {
      //   const [hour, minutes] = openingHourToPersist.open.split(":");
      //   openingHourToPersist.open = ChamaDate.fromHoursAndMinutes(
      //     +hour,
      //     +minutes,
      //   ).toPersistanceHoursAndMinutes();
      // }

      openingHoursToPersist.push(openingHourToPersist);
    });

    const { dealerId, ...openingHoursPayload } = data;

    await logisticsApi.put(
      `/v1/dealers/${dealerId}/coverage-areas/${data.coverageAreaId}/opening-hours`,
      {
        ...openingHoursPayload,
        openingHours: openingHoursToPersist,
      },
    );

    return data;
  } catch (err) {
    toast(i18next.t("failedUpdateSchedulesPleaseAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateMapRequest = createAsyncThunk<null, UpdateMapRequest>(
  "putMapLink",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setIsSpinnerVisible(true));

      await logisticsApi.put(
        `/v1/dealers/${data.dealerId}/coverage-areas/${data.coverageAreaId}/map`,
        { mapLink: data.mapLink },
      );

      await dispatch(
        handleGetWorkingAreaRequest({
          dealerId: data.dealerId,
          coverageAreaId: data.coverageAreaId,
        }),
      );

      return null;
    } catch (err) {
      toast(i18next.t("failedUpdateMapPleaseAgain").toString(), {
        type: "error",
        autoClose: false,
      });

      if (Axios.isAxiosError(err) && err && err.response) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err);
    } finally {
      dispatch(setIsSpinnerVisible(false));
    }
  },
);

interface CoverageAreaOrderDetailRequest {
  dealerId: string;
  coverageAreaId: string;
}

export const handleGetCoverageAreaOrderDetailRequest = createAsyncThunk<
  CoverageAreaOrderDetailDto,
  CoverageAreaOrderDetailRequest
>("getCoverageAreaOrderDetail", async (params, { rejectWithValue }) => {
  try {
    const { data } = await logisticsApi.get<CoverageAreaOrderDetailResponse>(
      `/v1/dealers/${params.dealerId}/coverage-areas/${params.coverageAreaId}`,
    );

    const normalized = normalizeCoverageAreaOrderDetail(data);
    if (!normalized) {
      return rejectWithValue(data);
    }

    return normalized;
  } catch (err) {
    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  }
});

export const handleMassUpdateWorkingHoursRequest = createAsyncThunk<
  MassUpdateWorkingHoursResponse,
  MassUpdateWorkingHoursRequest
>("putMassWorkingHours", async (data, { rejectWithValue }) => {
  try {
    await logisticsApi.put(`/v1/dealers/${data.dealerId}/coverage-areas`, {
      coverageAreas: data.coverageAreas,
    });

    return {
      data: {
        coverageAreas: data.coverageAreas,
      },
    };
  } catch (err) {
    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  }
});

export const handleSaveMapAreaDriversRequest = createAsyncThunk<
  void,
  SaveMapAreaDriversRequest
>("saveMapAreaDrivers", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await putCoverageAreaDrivers(
      data.dealerId,
      data.coverageAreaId,
      data.selectedDriverIdsInOrder,
    );

    toast(i18next.t("cardDriverEditSaveSuccess").toString(), {
      type: "success",
    });

    return undefined;
  } catch (err) {
    toast(i18next.t("cardDriverEditSaveError").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});
